import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ContentUploadModal } from "@/components/ContentUploadModal";
import { Check, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import type { User } from "@shared/schema";
import {
  CHECKIN_RETURN_KEY, CHECKIN_SKIP_KEY, CHECKIN_STEPS, PHOTOS_PER_PILLAR, PHOTOS_TOTAL,
  checkinProgress, firstOpenStep, showCheckinUploadQr, stepDone, type CheckinStatus,
} from "@/lib/checkin";

// Check-in steps 1-6 (plan Rev. 3 items 27-34, approved draft 03.10.). Start page for companies whose
// check-in is not hidden. Every step renders from GET /api/onboarding/status on its own; a step the server
// reports as "unavailable" only says so inside that step. Polls every 30 s while the tab is visible.

const POLL_MS = 30_000;

type HouseProfile = { guestsLove: string; offers: string; season: string; story: string };
type CompanyLite = {
  name?: string; website?: string | null; houseProfile?: Partial<HouseProfile> | null;
  dfyProfile?: string | null; edition?: { kind?: string } | null;
};

function setSession(key: string, value: string) {
  try { window.sessionStorage.setItem(key, value); } catch { /* private mode: flag is a convenience only */ }
}

const Unavailable = () => (
  <p className="text-sm text-muted-foreground" data-testid="text-step-unavailable">Dieser Schritt ist gerade nicht verfügbar. Bitte versuchen Sie es später erneut.</p>
);

export default function CheckInSteps() {
  const [, setLocation] = useLocation();
  const { data: user } = useQuery<User>({ queryKey: ["/api/auth/me"] });
  const { data: company } = useQuery<CompanyLite>({ queryKey: ["/api/companies/me"] });
  const { data: status, isError } = useQuery<CheckinStatus>({
    queryKey: ["/api/onboarding/status"],
    refetchInterval: POLL_MS, // react-query pauses this while the tab is hidden
    retry: false,
  });
  const [step, setStep] = useState<number | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadPillar, setUploadPillar] = useState<string | undefined>(undefined);
  const openUpload = (pillar?: string) => { setUploadPillar(pillar); setUploadOpen(true); };
  const current = step ?? firstOpenStep(status);
  const done = stepDone(status);
  const progress = checkinProgress(status);

  const dismiss = useMutation({
    mutationFn: () => apiRequest("PATCH", "/api/onboarding/checklist", { dismissed: true }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/companies/me"] });
      setLocation("/");
    },
  });

  const toDashboard = () => {
    setSession(CHECKIN_SKIP_KEY, "1");
    setLocation("/");
  };

  const s = status ?? {};
  const pillars = s.strategy && "pillars" in s.strategy ? s.strategy.pillars : [];

  const bodies: Record<number, ReactNode> = {
    1: (
      <div className="space-y-2">
        <p className="text-sm">
          <strong>{company?.name ?? "Ihr Hotel"}</strong>
          {company?.website ? <span className="text-muted-foreground"> · {company.website}</span> : null}
        </p>
        <p className="text-sm text-muted-foreground">Gespeichert. Ihr Team liest jetzt Ihre Website und recherchiert.</p>
        <Link href="/profil" className="text-sm underline">Angaben im Firmenprofil ändern</Link>
      </div>
    ),
    2: !s.channels || s.channels.state === "unavailable" ? <Unavailable /> : (
      <div className="space-y-2">
        {([
          ["Instagram", s.channels.instagram, false],
          ["Facebook", s.channels.facebook, false],
          ["LinkedIn", s.channels.linkedin, true],
        ] as const).map(([name, ok, optional]) => (
          <div key={name} className={`flex items-center justify-between rounded-md border px-3 py-2.5 ${ok ? "border-green-600 bg-green-50" : ""}`}>
            <span className="text-sm font-semibold">
              {name} {optional && <span className="font-normal text-muted-foreground">optional</span>}
            </span>
            {ok ? (
              <span className="flex items-center gap-1 text-sm text-green-700"><Check className="h-4 w-4" />Verbunden</span>
            ) : (
              <Button variant="outline" size="sm" data-testid={`button-connect-${name.toLowerCase()}`}
                onClick={() => { setSession(CHECKIN_RETURN_KEY, "1"); setLocation("/platform-connections"); }}>
                Verbinden
              </Button>
            )}
          </div>
        ))}
        <p className="text-xs text-muted-foreground">Wichtig im Meta-Fenster: Bitte wählen Sie alle Ihre Seiten aus, nicht nur eine.</p>
      </div>
    ),
    3: !s.strategy || s.strategy.state === "unavailable" ? <Unavailable /> : s.strategy.state === "ready" ? (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Lina hat {pillars.length} Themen gefunden, über die Ihr Hotel regelmäßig posten sollte.</p>
        <div className="grid gap-2">
          {pillars.map((p) => <div key={p} className="rounded-md border px-3 py-2 text-sm font-semibold">{p}</div>)}
        </div>
        <p className="text-xs text-muted-foreground">
          Anpassen können Sie die Themen unter <Link href="/marketingstrategie" className="underline">Marketing-Strategie</Link>.
        </p>
        <Button onClick={() => setStep(4)} data-testid="button-strategy-ok">Passt so, weiter zu den Fotos</Button>
      </div>
    ) : s.strategy.state === "failed" ? (
      <p className="text-sm">Bei Ihrer Strategie hakt es gerade. Ihr Team kümmert sich darum und meldet sich bei Ihnen.</p>
    ) : (
      <div className="space-y-2">
        <p className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin text-green-700" />Läuft, etwa 10 Minuten.</p>
        <p className="text-sm text-muted-foreground">Lina liest Ihre Website und recherchiert. Wir schicken Ihnen eine E-Mail, sobald Ihre Strategie fertig ist.</p>
      </div>
    ),
    4: !s.photos || s.photos.state === "unavailable" ? <Unavailable /> : (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {pillars.length > 0 ? `${PHOTOS_PER_PILLAR} Fotos pro Thema reichen für den Start.` : `${PHOTOS_TOTAL} Fotos reichen für den Start.`} Handyfotos sind völlig in Ordnung.
        </p>
        <div className="grid gap-2">
          {(pillars.length > 0 ? pillars : ["Ihre Fotos"]).map((p) => {
            const photos = s.photos as { total: number; byPillar: Record<string, number> };
            const count = pillars.length > 0 ? photos.byPillar[p] ?? 0 : photos.total;
            const goal = pillars.length > 0 ? PHOTOS_PER_PILLAR : PHOTOS_TOTAL;
            return (
              <div key={p} className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${count >= goal ? "border-green-600 bg-green-50" : ""}`}>
                <span className="text-sm font-semibold">{p}</span>
                <span className="flex items-center gap-3 text-sm text-muted-foreground">
                  {count} von {goal}
                  {pillars.length > 0 && (
                    <Button variant="outline" size="sm" disabled={!user} onClick={() => openUpload(p)} data-testid={`button-upload-pillar-${p}`}>
                      Fotos hinzufügen
                    </Button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-sm text-muted-foreground">Insgesamt {s.photos.total} {s.photos.total === 1 ? "Foto" : "Fotos"}. Ab {PHOTOS_TOTAL} Fotos ist dieser Schritt in jedem Fall erledigt.</p>
        {pillars.length === 0 && (
          <Button onClick={() => openUpload()} disabled={!user} data-testid="button-checkin-upload">Fotos hinzufügen</Button>
        )}
        {showCheckinUploadQr(company, user?.uploadToken) && (
          <div className="flex items-center gap-4 rounded-md bg-muted/50 p-3" data-testid="checkin-upload-qr">
            <QRCodeSVG value={`${window.location.origin}/upload/${user!.uploadToken}`} size={96} marginSize={1} aria-label="QR-Code für Ihren persönlichen Upload-Link" />
            <p className="text-sm text-muted-foreground">Tipp: Am Handy den QR-Code scannen und direkt aus der Galerie hochladen. Diese Fotos landen unter „Weitere Fotos“.</p>
          </div>
        )}
      </div>
    ),
    5: !s.house || s.house.state === "unavailable" ? <Unavailable /> : <HouseStep initial={company?.houseProfile} />,
    6: !s.firstWeek || s.firstWeek.state === "unavailable" ? <Unavailable /> : s.firstWeek.state === "done" ? (
      <p className="text-sm">Ihre erste Woche ist freigegeben. Ihr Team veröffentlicht die Beiträge wie geplant.</p>
    ) : s.firstWeek.state === "waiting" ? (
      <div className="space-y-2">
        <p className="text-sm">Ihre erste Content-Woche liegt zur Freigabe bereit.</p>
        <Button asChild data-testid="button-firstweek-review"><Link href="/wochenplan">Zur Freigabe</Link></Button>
      </div>
    ) : (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">Ihr Team erstellt jetzt Ihre ersten Beiträge aus Ihren Fotos und Themen. Sie bekommen eine E-Mail, sobald die Woche zur Freigabe bereitliegt.</p>
        <p className="flex items-center gap-2 text-sm"><span className="h-2 w-2 animate-pulse rounded-full bg-green-600" />Max schreibt, Emma gestaltet.</p>
      </div>
    ),
  };

  const meta = CHECKIN_STEPS[current - 1];

  // Until the first status arrives the open step is unknown; avoid flashing "Schritt 1".
  if (status === undefined && !isError) {
    return <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground animate-pulse">Lade Check-In...</div>;
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-green-700" data-testid="text-checkin-eyebrow">
            {"optional" in meta && meta.optional ? "Check-In, optional" : `Check-In, Schritt ${current} von 6`}
          </p>
          <h1 className="mt-1 font-playfair text-3xl font-bold">{current === 5 ? "Was Ihr Haus besonders macht" : meta.title}</h1>
        </div>
        <div className="min-w-[200px]">
          <span className="text-sm text-muted-foreground" data-testid="text-checkin-progress">{progress.done} von 6 erledigt</span>
          <div className="mt-1.5 h-2 overflow-hidden rounded bg-muted"><i className="block h-full bg-green-600 transition-all" style={{ width: `${(progress.done / 6) * 100}%` }} /></div>
        </div>
      </div>

      {isError && <p className="mt-4 text-sm text-muted-foreground">Der Stand Ihres Check-Ins ist gerade nicht verfügbar.</p>}

      <nav className="mt-5 flex gap-1.5 overflow-x-auto pb-1" aria-label="Check-In Schritte">
        {CHECKIN_STEPS.map((st) => (
          <button key={st.n} onClick={() => setStep(st.n)} data-testid={`checkin-step-${st.n}`}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs ${current === st.n ? "border-green-600 bg-green-50 font-semibold" : ""}`}>
            <span className={`grid h-4 w-4 place-items-center rounded-full border ${done[st.key] ? "border-green-600 bg-green-600 text-white" : ""}`}>
              {done[st.key] ? <Check className="h-3 w-3" /> : null}
            </span>
            {st.title}
            {"optional" in st && st.optional ? <span className="text-muted-foreground">optional</span> : null}
          </button>
        ))}
      </nav>

      <section className="mt-4 rounded-xl border bg-card p-5" data-testid={`checkin-body-${current}`}>{bodies[current]}</section>

      {current === 1 && (
        <p className="mt-4 text-sm text-orange-800"><strong>Start-Garantie:</strong> Ist Ihr Social Media A-Team nicht innerhalb von 7 Tagen startklar, ist Ihr erster Monat kostenlos.</p>
      )}

      <p className="mt-6 text-sm text-muted-foreground">
        Je mehr Sie uns im Check-In erzählen, desto besser werden Ihre Strategie und Ihre Beiträge. Sie können jederzeit hierher zurückkommen und ergänzen.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={toDashboard} data-testid="button-checkin-to-dashboard">Zum Dashboard</Button>
        <Button variant="ghost" onClick={() => dismiss.mutate()} disabled={dismiss.isPending} data-testid="button-checkin-dismiss">
          Check-In ausblenden
        </Button>
        {dismiss.isError && <span className="text-sm text-red-700">Das hat gerade nicht geklappt. Bitte versuchen Sie es erneut.</span>}
      </div>

      {user && <ContentUploadModal open={uploadOpen} onOpenChange={(o) => {
        setUploadOpen(o);
        if (!o) queryClient.invalidateQueries({ queryKey: ["/api/onboarding/status"] });
      }} user={user} pillarName={uploadPillar} origin="checkin" />}
    </div>
  );
}

const HOUSE_FIELDS: { key: keyof HouseProfile; label: string; example: string }[] = [
  { key: "guestsLove", label: "Was lieben Ihre Gäste?", example: "z. B. das Frühstück mit Produkten vom Hof" },
  { key: "offers", label: "Angebote und Pakete", example: "z. B. Wanderwochenende, Weinprobe" },
  { key: "season", label: "Saison und Veranstaltungen", example: "z. B. Weinfest im September" },
  { key: "story", label: "Geschichte oder Besonderheit", example: "z. B. seit 1890 in Familienhand" },
];

function HouseStep({ initial }: { initial?: Partial<HouseProfile> | null }) {
  const [values, setValues] = useState<HouseProfile>({ guestsLove: "", offers: "", season: "", story: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    if (initial) setValues((v) => ({ ...v, ...Object.fromEntries(Object.entries(initial).map(([k, x]) => [k, x ?? ""])) }));
  }, [initial]);
  const save = useMutation({
    mutationFn: () => apiRequest("PATCH", "/api/companies/me/house-profile", values),
    onSuccess: () => {
      setMsg({ ok: true, text: "Gespeichert. Ihr Team nutzt das für Ihre Beiträge." });
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies/me"] });
    },
    onError: (err: any) => setMsg({
      ok: false,
      text: err?.status === 400 && err?.data?.error
        ? err.data.error
        : "Das Speichern hat gerade nicht geklappt. Ihre Angaben sind noch da, bitte versuchen Sie es erneut.",
    }),
  });
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); setMsg(null); save.mutate(); }}>
      <p className="text-sm text-muted-foreground">Ein paar Stichworte genügen. Sie machen Strategie und Beiträge persönlicher.</p>
      {HOUSE_FIELDS.map((f) => (
        <div key={f.key} className="space-y-1.5">
          <Label htmlFor={`house-${f.key}`}>{f.label}</Label>
          <Textarea id={`house-${f.key}`} value={values[f.key]} maxLength={500} placeholder={f.example} className="min-h-[64px]"
            onChange={(e) => { const v = e.target.value; setValues((x) => ({ ...x, [f.key]: v })); setMsg(null); }}
            data-testid={`textarea-house-${f.key}`} />
        </div>
      ))}
      {msg && <p className={`text-sm ${msg.ok ? "text-green-800" : "text-red-700"}`} role={msg.ok ? "status" : "alert"}>{msg.text}</p>}
      <Button type="submit" disabled={save.isPending} data-testid="button-house-save">
        {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Speichern"}
      </Button>
    </form>
  );
}
